import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getUnit, Concept } from '../data/units';
import { getConceptProgress, getUnitProgress } from '../data/progress';
import { getPrerequisiteNames } from '../data/prerequisites';

const UnitScreen: React.FC = () => {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();

  const unit = unitId ? getUnit(unitId) : undefined;

  if (!unit) {
    return (
      <NotFoundContainer>
        <NotFoundCard>
          <NotFoundIcon>?</NotFoundIcon>
          <NotFoundTitle>Unit Not Found</NotFoundTitle>
          <NotFoundText>
            The unit you're looking for doesn't exist or may have been removed.
          </NotFoundText>
          <HomeLink onClick={() => navigate('/')}>Back to Home</HomeLink>
        </NotFoundCard>
      </NotFoundContainer>
    );
  }

  const conceptIds = unit.concepts.map((c) => c.id);
  const unitProgress = getUnitProgress(unit.id, conceptIds);
  const progressPercent =
    unitProgress.total > 0
      ? Math.round((unitProgress.completed / unitProgress.total) * 100)
      : 0;

  const getNextAction = (concept: Concept): string => {
    const progress = getConceptProgress(unit.id, concept.id);
    if (!progress.lessonCompleted) return 'Start Learning';
    if (progress.quizScores.length === 0) return 'Take Quiz';
    return 'Review';
  };

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/')}>
        <BackArrow>&larr;</BackArrow>
        All Units
      </BackButton>

      <UnitHeader $color={unit.color}>
        <HeaderContent>
          <UnitIcon>{unit.icon}</UnitIcon>
          <HeaderText>
            <UnitNumber $color={unit.color}>Unit {unit.number}</UnitNumber>
            <UnitTitle>{unit.title}</UnitTitle>
            <UnitDescription>{unit.description}</UnitDescription>
          </HeaderText>
        </HeaderContent>
      </UnitHeader>

      <ProgressSection>
        <ProgressHeader>
          <ProgressLabel>
            <strong>
              {unitProgress.completed} of {unitProgress.total}
            </strong>{' '}
            concepts completed
          </ProgressLabel>
          <ProgressPercent $color={unit.color}>{progressPercent}%</ProgressPercent>
        </ProgressHeader>
        <ProgressBarTrack>
          <ProgressBarFill $color={unit.color} $percent={progressPercent} />
        </ProgressBarTrack>
        {unitProgress.avgScore > 0 && (
          <AvgScore>
            Average quiz score: <strong>{unitProgress.avgScore}%</strong>
          </AvgScore>
        )}
      </ProgressSection>

      <ConceptsList>
        {unit.concepts.map((concept, index) => {
          const progress = getConceptProgress(unit.id, concept.id);
          const nextAction = getNextAction(concept);
          const prereqNames = getPrerequisiteNames(concept.id);

          return (
            <ConceptCard key={concept.id}>
              <ConceptIndex $color={unit.color} $completed={progress.lessonCompleted}>
                {progress.lessonCompleted ? (
                  <CheckIcon>&#10003;</CheckIcon>
                ) : (
                  <span>{index + 1}</span>
                )}
              </ConceptIndex>

              <ConceptBody>
                <ConceptTop>
                  <ConceptInfo>
                    <ConceptTitle>{concept.title}</ConceptTitle>
                    <ConceptDescription>{concept.description}</ConceptDescription>
                    {prereqNames.length > 0 && (
                      <PrereqHint>
                        <PrereqIcon>🔗</PrereqIcon>
                        Builds on: {prereqNames.join(', ')}
                      </PrereqHint>
                    )}
                    <ConceptMeta>
                      <TimeBadge>
                        <ClockIcon>&#9201;</ClockIcon>
                        {concept.estimatedMinutes} min
                      </TimeBadge>
                      {progress.bestQuizScore > 0 && (
                        <ScoreBadge $score={progress.bestQuizScore}>
                          Best: {progress.bestQuizScore}%
                        </ScoreBadge>
                      )}
                      {progress.lessonCompleted && !progress.bestQuizScore && (
                        <StatusBadge $variant="completed">Lesson done</StatusBadge>
                      )}
                      {!progress.lessonCompleted && progress.lessonStep > 0 && (
                        <StatusBadge $variant="in-progress">In progress</StatusBadge>
                      )}
                    </ConceptMeta>
                  </ConceptInfo>
                  <NextActionHint $color={unit.color}>{nextAction}</NextActionHint>
                </ConceptTop>

                <ActionButtons>
                  <ActionButton
                    $variant="learn"
                    $color={unit.color}
                    $primary={!progress.lessonCompleted}
                    onClick={() =>
                      navigate(`/unit/${unit.id}/concept/${concept.id}/lesson`)
                    }
                  >
                    <ButtonIcon>&#128214;</ButtonIcon>
                    {progress.lessonCompleted ? 'Review' : 'Learn'}
                  </ActionButton>
                  <ActionButton
                    $variant="quiz"
                    $color={unit.color}
                    $primary={progress.lessonCompleted && progress.quizScores.length === 0}
                    onClick={() =>
                      navigate(`/unit/${unit.id}/concept/${concept.id}/quiz`)
                    }
                  >
                    <ButtonIcon>&#9997;</ButtonIcon>
                    Quiz
                  </ActionButton>
                  <ActionButton
                    $variant="simulate"
                    $color={unit.color}
                    $primary={false}
                    onClick={() =>
                      navigate(`/unit/${unit.id}/concept/${concept.id}/simulation`)
                    }
                  >
                    <ButtonIcon>&#9881;</ButtonIcon>
                    Simulate
                  </ActionButton>
                </ActionButtons>
              </ConceptBody>
            </ConceptCard>
          );
        })}
      </ConceptsList>
    </PageContainer>
  );
};

export default UnitScreen;

/* ─── Styled Components ────────────────────────────────────────────── */

const PageContainer = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 20px 64px;
  background: #ffffff;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #5f6368;
  font-size: 15px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f1f3f4;
    color: #202124;
  }
`;

const BackArrow = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const UnitHeader = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color}0D;
  border: 1px solid ${({ $color }) => $color}22;
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const UnitIcon = styled.div`
  font-size: 48px;
  line-height: 1;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const UnitNumber = styled.div<{ $color: string }>`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${({ $color }) => $color};
  margin-bottom: 4px;
`;

const UnitTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #202124;
  margin: 0 0 8px;
  line-height: 1.2;
`;

const UnitDescription = styled.p`
  font-size: 16px;
  color: #5f6368;
  margin: 0;
  line-height: 1.5;
`;

const ProgressSection = styled.div`
  background: #fafafa;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 32px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ProgressLabel = styled.span`
  font-size: 14px;
  color: #5f6368;

  strong {
    color: #202124;
  }
`;

const ProgressPercent = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #e8eaed;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $color: string; $percent: number }>`
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  transition: width 0.4s ease;
`;

const AvgScore = styled.div`
  font-size: 13px;
  color: #5f6368;
  margin-top: 10px;

  strong {
    color: #202124;
  }
`;

const ConceptsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ConceptCard = styled.div`
  display: flex;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d0d0d0;
  }
`;

const ConceptIndex = styled.div<{ $color: string; $completed: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  margin-top: 2px;
  background: ${({ $completed, $color }) =>
    $completed ? $color : 'transparent'};
  color: ${({ $completed, $color }) => ($completed ? '#ffffff' : $color)};
  border: 2px solid ${({ $color }) => $color};
  transition: background 0.2s, color 0.2s;
`;

const CheckIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const ConceptBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConceptTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const ConceptInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConceptTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #202124;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const ConceptDescription = styled.p`
  font-size: 14px;
  color: #5f6368;
  margin: 0 0 10px;
  line-height: 1.45;
`;

const ConceptMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TimeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #80868b;
  background: #f1f3f4;
  padding: 3px 10px;
  border-radius: 12px;
`;

const ClockIcon = styled.span`
  font-size: 12px;
  line-height: 1;
`;

const ScoreBadge = styled.span<{ $score: number }>`
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  background: ${({ $score }) =>
    $score >= 80 ? '#e6f4ea' : $score >= 60 ? '#fef7e0' : '#fce8e6'};
  color: ${({ $score }) =>
    $score >= 80 ? '#1e8e3e' : $score >= 60 ? '#e37400' : '#d93025'};
`;

const StatusBadge = styled.span<{ $variant: 'completed' | 'in-progress' }>`
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
  background: ${({ $variant }) =>
    $variant === 'completed' ? '#e6f4ea' : '#e8f0fe'};
  color: ${({ $variant }) =>
    $variant === 'completed' ? '#1e8e3e' : '#1a73e8'};
`;

const NextActionHint = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  white-space: nowrap;
  padding: 4px 12px;
  border-radius: 12px;
  background: ${({ $color }) => $color}0F;
  flex-shrink: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{
  $variant: 'learn' | 'quiz' | 'simulate';
  $color: string;
  $primary: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  border: ${({ $primary, $color }) =>
    $primary ? 'none' : `1.5px solid ${$color}33`};
  background: ${({ $primary, $color }) =>
    $primary ? $color : '#ffffff'};
  color: ${({ $primary, $color }) =>
    $primary ? '#ffffff' : $color};

  &:hover {
    background: ${({ $primary, $color }) =>
      $primary ? $color + 'dd' : $color + '0D'};
    box-shadow: ${({ $primary }) =>
      $primary ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonIcon = styled.span`
  font-size: 14px;
  line-height: 1;
`;

const PrereqHint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #90a4ae;
  margin-top: 6px;
  line-height: 1.4;
`;

const PrereqIcon = styled.span`
  font-size: 12px;
  flex-shrink: 0;
`;

/* ─── Not Found Styles ─────────────────────────────────────────────── */

const NotFoundContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
`;

const NotFoundCard = styled.div`
  text-align: center;
  padding: 48px 40px;
  max-width: 400px;
`;

const NotFoundIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f3f4;
  color: #80868b;
  font-size: 28px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const NotFoundTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #202124;
  margin: 0 0 8px;
`;

const NotFoundText = styled.p`
  font-size: 15px;
  color: #5f6368;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const HomeLink = styled.button`
  display: inline-block;
  padding: 10px 28px;
  background: #1a73e8;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1557b0;
  }
`;
